import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, Plus, Trash2, X, Search, CalendarDays, Download } from "lucide-react";
import api, { API_URL, formatApiError, getToken } from "@/lib/api";
import { toast } from "sonner";

const MONTHS = ["Januari", "Februari", "Maret", "April", "Mei", "Juni", "Juli", "Agustus", "September", "Oktober", "November", "Desember"];
const DAYS_SHORT = ["Min", "Sen", "Sel", "Rab", "Kam", "Jum", "Sab"];
const TAHUN_AJARAN_OPTS = ["2026/2027", "2027/2028", "2028/2029"];

const PALETTE = [
  "#6366f1", "#ec4899", "#f59e0b", "#10b981", "#06b6d4",
  "#8b5cf6", "#ef4444", "#84cc16", "#f97316", "#14b8a6",
];

function pad(n) { return String(n).padStart(2, "0"); }
function ymd(y, m, d) { return `${y}-${pad(m + 1)}-${pad(d)}`; }
function colorFor(kegiatan) {
  let h = 0;
  for (let i = 0; i < kegiatan.length; i++) h = (h * 31 + kegiatan.charCodeAt(i)) >>> 0;
  return PALETTE[h % PALETTE.length];
}
// Tahun ajaran "2026/2027" → range Juli 2026 .. Juni 2027
function tahunAjaranRange(ta) {
  const [a, b] = ta.split("/").map(Number);
  return { start: `${a}-07-01`, end: `${b}-06-30` };
}
// Default tahun ajaran berdasar bulan saat ini (>=Juli pakai ta sekarang/berikut)
function currentTahunAjaran() {
  const t = new Date();
  const y = t.getFullYear();
  const m = t.getMonth(); // 0-11
  const start = m >= 6 ? y : y - 1;
  const ta = `${start}/${start + 1}`;
  return TAHUN_AJARAN_OPTS.includes(ta) ? ta : TAHUN_AJARAN_OPTS[0];
}

/**
 * Kalender Pendidikan — shared component
 * Props:
 *  - canEdit: bool — kepsek bisa input/hapus, role lain read-only
 */
export default function KalenderPendidikan({ canEdit = false }) {
  const today = new Date();
  const [tahunAjaran, setTahunAjaran] = useState(currentTahunAjaran());
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth());
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [appliedQ, setAppliedQ] = useState("");
  const [show, setShow] = useState(false);
  const [edit, setEdit] = useState(null);

  const load = useCallback(async () => {
    try {
      const params = { tahun_ajaran: tahunAjaran };
      if (appliedQ) params.q = appliedQ;
      const { data } = await api.get("/kalender-pendidikan", { params });
      setItems(data);
    } catch (e) { console.error(e); }
  }, [tahunAjaran, appliedQ]);
  useEffect(() => { load(); }, [load]);

  const doSearch = () => setAppliedQ(q.trim());
  const resetSearch = () => { setQ(""); setAppliedQ(""); };

  // Saat tahun ajaran berubah, lompat ke Juli tahun ajaran
  useEffect(() => {
    const [a] = tahunAjaran.split("/").map(Number);
    // Jika current view di luar range tahun ajaran, set ke Juli
    const view = new Date(year, month, 1);
    const start = new Date(a, 6, 1);
    const end = new Date(a + 1, 5, 30);
    if (view < start || view > end) { setYear(a); setMonth(6); }
  }, [tahunAjaran]);  

  // Build map tanggal → array kegiatan
  const dateMap = useMemo(() => {
    const m = {};
    items.forEach((it) => {
      const s = new Date(it.tanggal_mulai);
      const e = new Date(it.tanggal_selesai);
      for (let d = new Date(s); d <= e; d.setDate(d.getDate() + 1)) {
        const k = ymd(d.getFullYear(), d.getMonth(), d.getDate());
        if (!m[k]) m[k] = [];
        m[k].push(it);
      }
    });
    return m;
  }, [items]);

  const firstDay = new Date(year, month, 1);
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const startWeekday = firstDay.getDay();

  const prev = () => {
    if (month === 0) { setMonth(11); setYear(year - 1); }
    else setMonth(month - 1);
  };
  const next = () => {
    if (month === 11) { setMonth(0); setYear(year + 1); }
    else setMonth(month + 1);
  };
  const goToday = () => { setYear(today.getFullYear()); setMonth(today.getMonth()); };

  const cells = [];
  for (let i = 0; i < startWeekday; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) {
    const dateStr = ymd(year, month, d);
    cells.push({ day: d, dateStr, events: dateMap[dateStr] || [] });
  }
  while (cells.length % 7 !== 0) cells.push(null);

  // Filter list bawah: hanya yang overlap dengan bulan/tahun saat ini
  const monthEvents = useMemo(() => {
    const monthStart = ymd(year, month, 1);
    const monthEnd = ymd(year, month, daysInMonth);
    return items.filter((it) => !(it.tanggal_selesai < monthStart || it.tanggal_mulai > monthEnd));
  }, [items, year, month, daysInMonth]);

  const del = async (id) => {
    if (!window.confirm("Hapus kegiatan ini?")) return;
    try { await api.delete(`/kalender-pendidikan/${id}`); toast.success("Dihapus"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Gagal"); }
  };

  return (
    <div className="space-y-5">
      {/* Toolbar: tahun ajaran + search + tambah */}
      <section className="pp-card p-4 sm:p-5 flex flex-wrap items-end gap-3">
        <div className="min-w-[180px]">
          <label className="pp-label">Tahun Ajaran</label>
          <select className="pp-input" value={tahunAjaran} onChange={(e) => setTahunAjaran(e.target.value)} data-testid="kp-tahun-ajaran">
            {TAHUN_AJARAN_OPTS.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        <div className="flex-1 min-w-[220px]">
          <label className="pp-label">Cari Kegiatan</label>
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                className="pp-input pl-9"
                placeholder="ujian semester I, MPLS, ..."
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); doSearch(); } }}
                data-testid="kp-search"
              />
            </div>
            <button type="button" onClick={doSearch} className="pp-btn-primary" data-testid="kp-search-btn">
              <Search className="w-4 h-4" /> Cari
            </button>
            {appliedQ && (
              <button type="button" onClick={resetSearch} className="pp-btn-secondary" data-testid="kp-search-reset">
                Reset
              </button>
            )}
          </div>
        </div>
        {canEdit && (
          <button onClick={() => { setEdit(null); setShow(true); }} className="pp-btn-primary" data-testid="kp-add">
            <Plus className="w-4 h-4" /> Tambah Kegiatan
          </button>
        )}
        <button onClick={() => downloadKalenderPdf(tahunAjaran)} className="pp-btn-secondary" data-testid="kp-download-pdf">
          <Download className="w-4 h-4" /> Download PDF
        </button>
      </section>

      {/* Calendar */}
      <section className="pp-card p-5 sm:p-6" data-testid="kp-calendar">
        <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
          <div>
            <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
              <CalendarDays className="w-5 h-5 text-indigo-600" />
              {MONTHS[month]} {year}
            </h3>
            <p className="text-xs text-slate-500 mt-0.5">Kalender Pendidikan TA {tahunAjaran}</p>
          </div>
          <div className="flex items-center gap-1">
            <button onClick={prev} className="w-9 h-9 rounded-lg border border-slate-300 hover:bg-slate-50 flex items-center justify-center" data-testid="kp-prev"><ChevronLeft className="w-4 h-4" /></button>
            <button onClick={goToday} className="px-3 h-9 rounded-lg border border-slate-300 hover:bg-slate-50 text-xs font-medium" data-testid="kp-today">Hari ini</button>
            <button onClick={next} className="w-9 h-9 rounded-lg border border-slate-300 hover:bg-slate-50 flex items-center justify-center" data-testid="kp-next"><ChevronRight className="w-4 h-4" /></button>
          </div>
        </div>

        {/* Day labels */}
        <div className="grid grid-cols-7 gap-1 mb-1">
          {DAYS_SHORT.map((d) => (
            <div key={d} className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold text-center py-1.5">{d}</div>
          ))}
        </div>

        <div className="grid grid-cols-7 gap-1">
          {cells.map((c, i) => {
            if (!c) return <div key={`e-${i}`} className="aspect-square" />;
            const isToday = c.dateStr === ymd(today.getFullYear(), today.getMonth(), today.getDate());
            const hasEvent = c.events.length > 0;
            const mainColor = hasEvent ? colorFor(c.events[0].kegiatan) : null;
            return (
              <div
                key={c.dateStr}
                className={`aspect-square rounded-lg border ${hasEvent ? "" : "border-transparent bg-slate-50"} ${isToday ? "ring-2 ring-indigo-500 ring-offset-1" : ""} flex flex-col items-center justify-center text-xs relative overflow-hidden`}
                style={hasEvent ? { backgroundColor: `${mainColor}22`, borderColor: `${mainColor}80`, color: mainColor } : undefined}
                data-testid={`kp-day-${c.dateStr}`}
                title={hasEvent ? c.events.map(e => e.kegiatan).join(", ") : "Tidak ada kegiatan"}
              >
                <span className="font-semibold">{c.day}</span>
                {c.events.length > 0 && (
                  <span className="absolute bottom-0.5 left-0 right-0 text-[8px] truncate px-1 font-medium opacity-90" style={{ color: mainColor }}>
                    {c.events[0].kegiatan}
                  </span>
                )}
                {c.events.length > 1 && (
                  <span className="absolute top-0.5 right-0.5 w-3.5 h-3.5 rounded-full bg-rose-500 text-white text-[8px] font-bold flex items-center justify-center">+{c.events.length - 1}</span>
                )}
              </div>
            );
          })}
        </div>
      </section>

      {/* Keterangan: daftar kegiatan di bulan ini */}
      <section className="pp-card p-5">
        <h4 className="font-semibold text-slate-900 mb-3">Keterangan — {MONTHS[month]} {year}</h4>
        {monthEvents.length === 0 ? (
          <p className="text-sm text-slate-400 italic">Tidak ada kegiatan di bulan ini.</p>
        ) : (
          <ul className="space-y-2">
            {monthEvents.map((it) => (
              <li key={it.id} className="flex items-start gap-3 p-3 rounded-lg border border-slate-200 hover:bg-slate-50" data-testid={`kp-event-${it.id}`}>
                <span className="mt-1.5 w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: colorFor(it.kegiatan) }} />
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-slate-900">{it.kegiatan}</div>
                  <div className="text-xs text-slate-500">
                    {it.tanggal_mulai === it.tanggal_selesai
                      ? formatLong(it.tanggal_mulai)
                      : `${formatLong(it.tanggal_mulai)} — ${formatLong(it.tanggal_selesai)}`}
                  </div>
                  {it.keterangan && <div className="text-sm text-slate-700 mt-1">{it.keterangan}</div>}
                </div>
                {canEdit && (
                  <div className="flex gap-1 shrink-0">
                    <button onClick={() => { setEdit(it); setShow(true); }} className="pp-btn-ghost text-xs" data-testid={`kp-edit-${it.id}`}>Edit</button>
                    <button onClick={() => del(it.id)} className="pp-btn-danger text-xs" data-testid={`kp-del-${it.id}`}><Trash2 className="w-3 h-3" /></button>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>

      {show && canEdit && (
        <EventForm
          edit={edit}
          tahunAjaran={tahunAjaran}
          setTahunAjaran={setTahunAjaran}
          onClose={() => { setShow(false); setEdit(null); }}
          onSaved={() => { setShow(false); setEdit(null); load(); }}
        />
      )}
    </div>
  );
}

function formatLong(yyyymmdd) {
  if (!yyyymmdd) return "—";
  const [y, m, d] = yyyymmdd.split("-");
  return `${parseInt(d, 10)} ${MONTHS[parseInt(m, 10) - 1]} ${y}`;
}

function downloadKalenderPdf(tahunAjaran) {
  const params = new URLSearchParams({ tahun_ajaran: tahunAjaran });
  const url = `${API_URL}/export/kalender-pendidikan?${params.toString()}`;
  const t = getToken();
  fetch(url, { headers: t ? { Authorization: `Bearer ${t}` } : {}, credentials: "include" })
    .then((r) => r.blob())
    .then((blob) => {
      const u = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = u; a.download = `kalender-pendidikan-${tahunAjaran.replace("/", "-")}.pdf`;
      a.click(); URL.revokeObjectURL(u);
    });
}

function EventForm({ edit, tahunAjaran, setTahunAjaran, onClose, onSaved }) {
  const range = tahunAjaranRange(tahunAjaran);
  const [form, setForm] = useState(() => edit ? { ...edit } : {
    tahun_ajaran: tahunAjaran,
    tanggal_mulai: range.start,
    tanggal_selesai: range.start,
    kegiatan: "",
    keterangan: "",
  });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (form.tanggal_selesai < form.tanggal_mulai) {
      toast.error("Tanggal selesai harus >= tanggal mulai"); return;
    }
    setBusy(true);
    try {
      if (edit) await api.put(`/kalender-pendidikan/${edit.id}`, form);
      else await api.post("/kalender-pendidikan", form);
      toast.success(edit ? "Diperbarui" : "Disimpan");
      if (form.tahun_ajaran !== tahunAjaran) setTahunAjaran(form.tahun_ajaran);
      onSaved();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Gagal"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="kp-form-modal">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-slate-900">{edit ? "Edit Kegiatan" : "Tambah Kegiatan"}</h3>
          <button onClick={onClose}><X className="w-5 h-5 text-slate-400" /></button>
        </div>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <label className="pp-label">Tahun Ajaran *</label>
            <select required className="pp-input" value={form.tahun_ajaran} onChange={(e) => setForm({ ...form, tahun_ajaran: e.target.value })} data-testid="kp-form-ta">
              {TAHUN_AJARAN_OPTS.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
            <p className="text-[11px] text-slate-500 mt-1">TA {form.tahun_ajaran}: Juli {form.tahun_ajaran.split("/")[0]} – Juni {form.tahun_ajaran.split("/")[1]}</p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="pp-label">Tanggal Mulai *</label>
              <input required type="date" className="pp-input" value={form.tanggal_mulai} onChange={(e) => setForm({ ...form, tanggal_mulai: e.target.value })} data-testid="kp-form-mulai" />
            </div>
            <div>
              <label className="pp-label">Tanggal Selesai *</label>
              <input required type="date" className="pp-input" value={form.tanggal_selesai} onChange={(e) => setForm({ ...form, tanggal_selesai: e.target.value })} data-testid="kp-form-selesai" />
            </div>
          </div>
          <div>
            <label className="pp-label">Kegiatan *</label>
            <input required className="pp-input" placeholder="MPLS, Ujian Semester I, Libur Sekolah, ..." value={form.kegiatan} onChange={(e) => setForm({ ...form, kegiatan: e.target.value })} data-testid="kp-form-kegiatan" />
          </div>
          <div>
            <label className="pp-label">Keterangan (opsional)</label>
            <textarea rows={2} className="pp-input" placeholder="Detail tambahan..." value={form.keterangan || ""} onChange={(e) => setForm({ ...form, keterangan: e.target.value })} data-testid="kp-form-keterangan" />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="pp-btn-secondary">Batal</button>
            <button type="submit" disabled={busy} className="pp-btn-primary" data-testid="kp-form-save">{busy ? "Menyimpan…" : "Simpan"}</button>
          </div>
        </form>
      </div>
    </div>
  );
}
